import { useState, useEffect } from "react";
// @mui
import {
  Stack,
  Grid,
  Box,
  Alert,
  Typography,
  Container,
  TextField,
  CssBaseline,
  Button,
  MenuItem,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
// icons
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import DomainIcon from "@mui/icons-material/Domain";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
// components
import ChangePasswordForm from "../components/auth/ChangePasswordForm";
// utils
import axios from "../utils/axios";
import merchantCategoryOpts from "../utils/merchantCategoryOpts";
import countriesOpts from "../utils/countriesOpts";
// hooks
import useIsMountedRef from "../hooks/useIsMountedRef";
import useAuth from "../hooks/useAuth";
//
import * as Yup from "yup";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";

export default function UserProfile() {
  const isMountedRef = useIsMountedRef();
  const [user, setUser] = useState({
    name: "",
    surname: "",
    email: "",
    city: "",
    country: "",
    province: "",
    merchant_category: "",
    company: "",
  });
  const { updateProfile } = useAuth();
  const [successAlert, setSuccessAlert] = useState(null);
  const { t } = useTranslation();

  const theme = useTheme();

  const getProfile = async () => {
    axios
      .post("/auth/profile")
      .then((res) => {
        const user = res.data.user;
        setUser(user);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  useEffect(() => {
    getProfile();
  }, []);

  const RegisterSchema = Yup.object().shape({
    name: Yup.string().required(t("yup.errors.name_required")),
    surname: Yup.string().required(t("yup.errors.surname_required")),
    email: Yup.string()
      .email(t("yup.errors.email_not_valid"))
      .required(t("yup.errors.email_required")),
    city: Yup.string().required(t("yup.errors.city_required")),
    country: Yup.string().required(t("yup.errors.country_required")),
    province: Yup.string().nullable().notRequired(),
    merchant_category: Yup.string().required(
      t("yup.errors.merchant_category_required")
    ),
    company: Yup.string().nullable().notRequired(),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: user,
    validationSchema: RegisterSchema,
    onSubmit: async (values, { setErrors, setSubmitting, resetForm }) => {
      try {
        const response = await updateProfile(
          values.name,
          values.surname,
          values.email,
          values.city,
          values.country,
          values.province,
          values.merchant_category,
          values.company
        );
        if (isMountedRef.current) {
          setSubmitting(false);
        }

        // Update user state
        setUser(response.user);
        setSuccessAlert(t(response.message));
        setTimeout(() => {
          setSuccessAlert(null);
        }, 3000);
      } catch (error) {
        console.error(error);

        if (isMountedRef.current) {
          if (error.status === 422) {
            const errorMessage = Object.values(error.data)[0][0];
            setErrors({ afterSubmit: errorMessage });
          } else {
            setErrors({ afterSubmit: t(error.data.message) });
          }

          setSubmitting(false);
        }
        setTimeout(() => {
          setErrors({ afterSubmit: null });
        }, 3000);
      }
    },
  });

  const { errors } = formik;

  return (
    <Container component="div">
      <CssBaseline />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Typography
          component="h1"
          variant="h4"
          sx={{ color: "#004990", fontWeight: "bold", mb: 2 }}
        >
          {t("profile.title")}
        </Typography>

        <form onSubmit={formik.handleSubmit}>
          {errors.afterSubmit && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {errors.afterSubmit}
            </Alert>
          )}
          {successAlert && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {successAlert}
            </Alert>
          )}

          <Grid container spacing={2} justifyContent="center">
            <Grid item xs={12} md={6}>
              <TextField
                margin="normal"
                variant="outlined"
                fullWidth
                id="name"
                placeholder={t("authentication.fields.name")}
                name="name"
                autoComplete="name"
                autoFocus
                value={formik.values.name}
                onChange={formik.handleChange}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                sx={styles.inputText}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonOutlineOutlinedIcon sx={{ color: "#6369E9" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                margin="normal"
                variant="outlined"
                fullWidth
                id="surname"
                placeholder={t("authentication.fields.surname")}
                name="surname"
                autoComplete="surname"
                autoFocus
                value={formik.values.surname}
                onChange={formik.handleChange}
                error={formik.touched.surname && Boolean(formik.errors.surname)}
                helperText={formik.touched.surname && formik.errors.surname}
                sx={styles.inputText}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonOutlineOutlinedIcon sx={{ color: "#6369E9" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                margin="normal"
                variant="outlined"
                fullWidth
                id="email"
                placeholder={t("authentication.fields.email")}
                name="email"
                autoComplete="email"
                autoFocus
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                sx={styles.inputText}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MailOutlineIcon sx={{ color: "#6369E9" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                margin="normal"
                variant="outlined"
                fullWidth
                id="city"
                placeholder={t("authentication.fields.city")}
                name="city"
                autoComplete="city"
                autoFocus
                value={formik.values.city}
                onChange={formik.handleChange}
                error={formik.touched.city && Boolean(formik.errors.city)}
                helperText={formik.touched.city && formik.errors.city}
                sx={styles.inputText}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationCityIcon sx={{ color: "#6369E9" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                name="country"
                label={t("authentication.fields.country")}
                value={formik.values.country}
                onChange={formik.handleChange}
                error={formik.touched.country && Boolean(formik.errors.country)}
                helperText={formik.touched.country && formik.errors.country}
                sx={{
                  mt: 2,
                }}
              >
                {countriesOpts.map((item, index) => (
                  <MenuItem value={item.value} key={index}>
                    {item.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                margin="normal"
                variant="outlined"
                fullWidth
                id="province"
                placeholder={t("authentication.fields.province")}
                name="province"
                autoComplete="province"
                autoFocus
                value={formik.values.province}
                onChange={formik.handleChange}
                error={
                  formik.touched.province && Boolean(formik.errors.province)
                }
                helperText={formik.touched.province && formik.errors.province}
                sx={styles.inputText}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <DomainIcon sx={{ color: "#6369E9" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                name="merchant_category"
                label={t("authentication.fields.merchant_category")}
                value={formik.values.merchant_category}
                onChange={formik.handleChange}
                error={
                  formik.touched.merchant_category &&
                  Boolean(formik.errors.merchant_category)
                }
                helperText={
                  formik.touched.merchant_category &&
                  formik.errors.merchant_category
                }
                sx={{
                  mt: 2,
                }}
              >
                {merchantCategoryOpts.map((item, index) => (
                  <MenuItem value={item.value} key={index}>
                    {item.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                margin="normal"
                variant="outlined"
                fullWidth
                id="company"
                placeholder={t("authentication.fields.company")}
                name="company"
                autoComplete="company"
                autoFocus
                value={formik.values.company}
                onChange={formik.handleChange}
                error={formik.touched.company && Boolean(formik.errors.company)}
                helperText={formik.touched.company && formik.errors.company}
                sx={styles.inputText}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationCityIcon sx={{ color: "#6369E9" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}></Grid>
          </Grid>

          <Stack
            direction="column"
            justifyContent="center"
            spacing={3}
            sx={{ mt: 3, p: "0 25%", textAlign: "center" }}
          >
            <Button
              type="submit"
              variant="contained"
              sx={{ mb: 3, p: "8px 50px", backgroundColor: "#2CA4DE" }}
            >
              {t("profile.update.button")}
            </Button>
          </Stack>
        </form>

        <Grid container spacing={2} justifyContent="center">
          <Grid item xs={12} md={6}>
            <Accordion
              sx={{ border: "none !important", boxShadow: "none !important" }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header"
              >
                <Typography sx={{ color: theme.palette.primary.main }}>
                  {t("profile.update.change_password_link")}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <ChangePasswordForm />
              </AccordionDetails>
            </Accordion>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
}

const styles = {
  footerLink: {
    color: "#231F20",
    fontSize: "12px",
  },
};
